import {
  ModuleSurfaceOperatorConfig,
  defaultModuleSurfaceOperatorConfig
} from '../workflow-catalog.models';
import {
  MappingEntry,
  entriesToMapping,
  mappingToEntries,
  parseConfigJson,
  parseStringMapping,
  stringifyConfigJson
} from './workflow-operator-config.utils';
import { parsePartyKind } from '../../../shared/design-system/party-kind-options';

export function parseModuleSurfaceOperatorConfig(raw: string): ModuleSurfaceOperatorConfig {
  const parsed = parseConfigJson(raw, defaultModuleSurfaceOperatorConfig());
  return {
    moduleKey: `${parsed.moduleKey ?? ''}`.trim(),
    surfaceKey: `${parsed.surfaceKey ?? ''}`.trim(),
    surfaceMode: `${parsed.surfaceMode ?? ''}`.trim(),
    contextMapping: parseStringMapping(parsed.contextMapping),
    outputMapping: parseStringMapping(parsed.outputMapping),
    allowFullPageEscape: parsed.allowFullPageEscape !== false,
    fullPageRouteTemplate: `${parsed.fullPageRouteTemplate ?? ''}`.trim()
  };
}

export function buildModuleSurfaceConfigurationJson(model: ModuleSurfaceOperatorConfig): string {
  return stringifyConfigJson({
    moduleKey: model.moduleKey.trim(),
    surfaceKey: model.surfaceKey.trim(),
    surfaceMode: model.surfaceMode?.trim() || undefined,
    contextMapping: model.contextMapping,
    outputMapping: model.outputMapping,
    allowFullPageEscape: model.allowFullPageEscape,
    fullPageRouteTemplate: model.fullPageRouteTemplate.trim() || undefined
  });
}

export interface DesignerOrchestratorOperator {
  type: string;
  referenceType: string;
  referenceId: string | null;
  assigneeType: string | null;
  assigneeId: string | null;
  dueDays: number;
  dueHours: number;
  dueMinutes: number;
  configurationJson: string;
}

export function normalizeFormOperatorConfiguration(operator: DesignerOrchestratorOperator): string {
  if (operator.type !== 'Form') {
    return operator.configurationJson;
  }

  const referenceId = `${operator.referenceId ?? ''}`.trim();
  if (!referenceId) {
    return operator.configurationJson;
  }

  const config = parseConfigJson(operator.configurationJson, {} as Record<string, unknown>);
  return stringifyConfigJson({
    ...config,
    formDefinitionId: referenceId,
    referenceType: 'form',
    referenceId
  });
}

export function finalizeModuleSurfaceOperatorConfiguration(operator: DesignerOrchestratorOperator): string {
  const surfaceConfig = parseModuleSurfaceOperatorConfig(operator.configurationJson);
  const base = buildModuleSurfaceConfigurationJson(surfaceConfig);

  const config = parseConfigJson(base, {} as Record<string, unknown>);
  const partyKind = parsePartyKind(operator.assigneeType) ?? parsePartyKind(config['partyKind']);
  const partyId = `${operator.assigneeId ?? config['partyId'] ?? ''}`.trim() || null;

  return stringifyConfigJson({
    ...config,
    partyKind: partyKind ?? undefined,
    partyId,
    assigneeType: operator.assigneeType,
    assigneeId: operator.assigneeId,
    dueDays: operator.dueDays ?? 0,
    dueHours: operator.dueHours ?? 0,
    dueMinutes: operator.dueMinutes ?? 0
  });
}

export function finalizeUserTaskOperatorConfiguration(operator: DesignerOrchestratorOperator): string {
  const config = parseConfigJson(operator.configurationJson, {} as Record<string, unknown>);
  const partyKind = parsePartyKind(operator.assigneeType);
  const partyId = `${operator.assigneeId ?? config['partyId'] ?? ''}`.trim() || null;

  return stringifyConfigJson({
    ...config,
    partyKind: partyKind ?? undefined,
    partyId,
    assigneeType: operator.assigneeType,
    assigneeId: operator.assigneeId,
    dueDays: operator.dueDays ?? 0,
    dueHours: operator.dueHours ?? 0,
    dueMinutes: operator.dueMinutes ?? 0
  });
}

export function finalizeOperatorConfiguration(operator: DesignerOrchestratorOperator): string {
  if (operator.type === 'Form') {
    return normalizeFormOperatorConfiguration(operator);
  }

  if (operator.type === 'ModuleSurface') {
    return finalizeModuleSurfaceOperatorConfiguration(operator);
  }

  if (operator.type === 'UserTask') {
    return finalizeUserTaskOperatorConfiguration(operator);
  }

  return operator.configurationJson;
}

export function validateModuleSurfaceOperatorConfiguration(
  configurationJson: string
): { moduleKey?: string; surfaceKey?: string; valid: boolean } {
  const config = parseModuleSurfaceOperatorConfig(configurationJson);
  return {
    moduleKey: config.moduleKey,
    surfaceKey: config.surfaceKey,
    valid: !!config.moduleKey && !!config.surfaceKey
  };
}

export function moduleSurfaceContextMappingEntries(mapping: Record<string, string>): MappingEntry[] {
  return mappingToEntries(mapping);
}

export function moduleSurfaceContextMappingFromEntries(entries: MappingEntry[]): Record<string, string> {
  return entriesToMapping(entries);
}
